import { useEffect, useRef, useState } from 'react'
import ModalMainWrapper from './ModalMainWrapper'
import ModalContentWrapper from './ModalContentWrapper'
import ModalHeader from './ModalHeader'
import ModalContent from './ModalContent'

export default function DocxModal({ title, name, url }) {
    const modalTitle = title || name || 'DOCX Preview'
    const proxiedUrl = `/api/proxy-file?url=${encodeURIComponent(url)}`
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const containerRef = useRef(null)

    useEffect(() => {
        let isMounted = true
        const controller = new AbortController()

        async function load() {
            try {
                setLoading(true)
                setError(null)

                const res = await fetch(proxiedUrl, {
                    method: 'GET',
                    signal: controller.signal,
                })
                if (!res.ok) {
                    throw new Error(`Failed to load docx: ${res.status}`)
                }
                const buffer = await res.arrayBuffer()

                // dynamic import to avoid SSR issues
                const mod = await import('docx-preview')
                const renderAsync = mod.renderAsync || mod.default?.renderAsync
                if (!renderAsync) {
                    throw new Error('docx-preview renderAsync not available')
                }

                if (!isMounted) return
                // Render into container. Allow scroll inside container
                await renderAsync(buffer, containerRef.current, undefined, {
                    className: 'docx',
                })
            } catch (e) {
                if (!isMounted) return
                setError(e?.message || 'Failed to render DOCX')
            } finally {
                if (isMounted) setLoading(false)
            }
        }

        load()

        // Failsafe to remove loader if something hangs
        const t = setTimeout(() => {
            if (isMounted) setLoading(false)
        }, 12000)

        return () => {
            isMounted = false
            controller.abort()
            clearTimeout(t)
            if (containerRef.current) {
                containerRef.current.innerHTML = ''
            }
        }
    }, [proxiedUrl])

    return (
        <ModalMainWrapper>
            <ModalContentWrapper>
                <ModalHeader title={modalTitle} />
                <ModalContent>
                    <div className="w-full relative">
                        {loading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                                <div className="flex items-center space-x-2 text-slate-600">
                                    <svg
                                        className="animate-spin h-5 w-5 text-slate-500"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                    </svg>
                                    <span className="text-sm">
                                        Rendering document…
                                    </span>
                                </div>
                            </div>
                        )}
                        {error && (
                            <div className="mb-3 text-sm text-red-600">
                                {error}
                            </div>
                        )}
                        <div
                            ref={containerRef}
                            className="w-full h-[75vh] overflow-auto border border-slate-200 rounded p-3 bg-white"
                        />
                        <div className="mt-2 text-right">
                            <a
                                className="underline text-sm"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download>
                                Download original
                            </a>
                        </div>
                    </div>
                </ModalContent>
            </ModalContentWrapper>
        </ModalMainWrapper>
    )
}
